/**
 * useEscapeKey — global-stack Esc-to-close hook for custom (non-Radix)
 * modals. (2026-08-27, follow-up to the app-wide no-close-on-outside-
 * click policy.)
 *
 * Contract:
 *   const MyModal = ({ onClose }) => {
 *     useEscapeKey(onClose);
 *     return (<div className="fixed inset-0 …">…</div>);
 *   };
 *
 * Semantics:
 *   • Every mounted useEscapeKey call pushes its callback onto a LIFO
 *     stack. The stack is walked in reverse so the top-most modal
 *     always intercepts Escape first — this "just works" for nested
 *     modals (e.g. an alert layered over the Quick HA Sale modal).
 *   • A single window-level `keydown` listener drives the whole stack,
 *     so we never leak listeners even when dozens of modals mount and
 *     unmount over the app's lifetime.
 *   • Radix Dialog / Sheet / AlertDialog handle Escape natively via
 *     their own `onEscapeKeyDown` prop — those consumers do NOT call
 *     this hook. It is deliberately opt-in.
 *   • Text inputs inside a modal don't interfere: Escape is a browser-
 *     level key event that fires regardless of focus target.
 */
import { useEffect, useRef } from 'react';

// LIFO stack of active callback refs.
const escStack = [];
let listenerBound = false;

function bindWindowListener() {
  if (listenerBound || typeof window === 'undefined') return;
  listenerBound = true;
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (escStack.length === 0) return;
    // Walk the stack in reverse so the top-most modal wins.
    const topRef = escStack[escStack.length - 1];
    if (topRef && typeof topRef.current === 'function') {
      // Fire the modal's close handler. We intentionally do NOT call
      // e.preventDefault() — some hosts (Storybook, form submits) may
      // still want to observe the event.
      topRef.current();
    }
  });
}

/**
 * Register `onClose` as the top-most Escape handler while this component
 * is mounted. Pass `enabled=false` to temporarily disable (e.g. inside
 * a nested confirmation state).
 */
export function useEscapeKey(onClose, enabled = true) {
  const ref = useRef(onClose);
  // Keep the ref pointing at the latest callback — React re-creates
  // arrow-fn props on every render, so a naïve dependency array would
  // thrash the stack. Storing via ref decouples callback identity from
  // stack membership.
  ref.current = onClose;

  useEffect(() => {
    bindWindowListener();
    if (!enabled || typeof onClose !== 'function') return undefined;
    escStack.push(ref);
    return () => {
      const idx = escStack.lastIndexOf(ref);
      if (idx >= 0) escStack.splice(idx, 1);
    };
    // We deliberately depend ONLY on `enabled` — the ref stays stable
    // across renders so callback identity churn doesn't re-register.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}

export default useEscapeKey;
